// src/components/Landing/SystemCarousel.js
import React, { useState, useEffect, useRef } from 'react';

const SystemCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const totalSlides = 4; // Número total de slides
  const autoPlayRef = useRef(null);
  const timeoutRef = useRef(null);

  // Dados dos slides com caminhos corrigidos
  const slides = [
    {
      image: "/prints/dashboard.png", // Caminho correto relativo à pasta public
      title: "Dashboard Intuitivo",
      description: "Visualize todos os seus indicadores financeiros em um só lugar."
    },
    {
      image: "/prints/categorizar.png", // Caminho correto relativo à pasta public
      title: "Categorização Inteligente",
      description: "A IA aprende com suas transações e automaticamente classifica novos lançamentos, economizando horas de trabalho manual."
    },
    {
      image: "/prints/dre.png", // Caminho correto relativo à pasta public
      title: "Relatórios DRE Detalhados",
      description: "Relatórios profissionais com análises comparativas que mostram a evolução do seu negócio mês a mês."
    },
    {
      image: "/prints/grafico.png", // Caminho correto relativo à pasta public
      title: "Gráficos Interativos",
      description: "Acompanhe suas despesas com gráficos interativos e compare os gastos de cada mês de forma simples e visual."
    }
  ];

  // Navegar para o próximo slide
  const nextSlide = () => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev === totalSlides - 1 ? 0 : prev + 1));
    
    // Resetar estado de animação após a transição
    timeoutRef.current = setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  };

  // Navegar para o slide anterior
  const prevSlide = () => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev === 0 ? totalSlides - 1 : prev - 1));
    
    // Resetar estado de animação após a transição
    timeoutRef.current = setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  };

  // Ir para um slide específico
  const goToSlide = (index) => {
    if (isAnimating || index === currentSlide) return;
    
    setIsAnimating(true);
    setCurrentSlide(index);
    
    // Resetar estado de animação após a transição
    timeoutRef.current = setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  };

  // Configurar autoplay
  useEffect(() => {
    const play = () => {
      nextSlide();
    };

    autoPlayRef.current = play;
  }, [currentSlide, isAnimating]);

  // Iniciar o autoplay
  useEffect(() => {
    const interval = setInterval(() => {
      if (autoPlayRef.current) {
        autoPlayRef.current();
      }
    }, 5000); // Muda slide a cada 5 segundos

    return () => {
      clearInterval(interval);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <section className="lp-demo-section lp-carousel-section">
      <div className="lp-section-header lp-animate-on-scroll">
        <div className="lp-section-badge">Veja em Ação</div>
        <h2>Conheça o DRE Pro</h2>
        <p>Explore as capturas de tela para ver como nossa plataforma pode transformar a gestão financeira do seu negócio</p>
      </div>

      <div className="lp-carousel-container lp-animate-on-scroll">
        <div 
          className="lp-carousel-wrapper" 
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {slides.map((slide, index) => (
            <div key={index} className="lp-carousel-slide">
              <img 
                src={slide.image} 
                alt={slide.title} 
                className="lp-carousel-image" 
                // Fallback para imagem placeholder caso a imagem real não esteja disponível
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `https://via.placeholder.com/1200x675/1f2937/ffffff?text=DRE+Pro+-+${slide.title.replace(/\s+/g, '+')}`;
                }}
              />
              <div className="lp-carousel-caption">
                <h3>{slide.title}</h3>
                <p>{slide.description}</p>
              </div>
            </div>
          ))}
        </div>
        
        <button 
          className="lp-carousel-control lp-carousel-prev" 
          onClick={prevSlide}
          aria-label="Slide anterior"
        >
          <svg viewBox="0 0 24 24">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        
        <button 
          className="lp-carousel-control lp-carousel-next" 
          onClick={nextSlide}
          aria-label="Próximo slide"
        >
          <svg viewBox="0 0 24 24">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
        
        <div className="lp-carousel-indicators">
          {slides.map((_, index) => (
            <button
              key={index}
              className={`lp-carousel-indicator ${index === currentSlide ? 'lp-active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Ir para slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default SystemCarousel;